import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { clipboardService, adminService } from '../services/api';
import ClipboardItem from './ClipboardItem';
import LinkPreview from './LinkPreview';
import CommentSection from './CommentSection';
import ImageModal from './ImageModal';
import useInfiniteScroll from '../hooks/useInfiniteScroll';
import {
  Search,
  Filter,
  RefreshCw,
  Loader2,
  FileText,
  LogOut,
  Settings,
  Grid,
  List,
  Heart,
  Eye,
  Share2,
  Bookmark,
  Sparkles,
  Clock,
  TrendingUp,
  Hash,
  Tag,
  FolderOpen,
  Layers,
  Zap,
  Star,
  Users,
  Calendar,
  MapPin,
  Plus,
  MoreHorizontal,
  UserPlus,
  MessageCircle,
  Award,
  TrendingDown,
  Code,
  Palette,
  ChevronUp,
  ChevronDown,
  Music,
  Camera,
  BookOpen,
  Gamepad2,
  Globe,
  ZoomIn
} from 'lucide-react';
import HorizontalVoteButtons from './HorizontalVoteButtons';
import toast from 'react-hot-toast';

const PublicPage5 = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('all');
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [filters, setFilters] = useState({
    content_type: '',
    sort_by: 'latest'
  });
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_items: 0,
    items_per_page: 20,
  });
  const [hasMore, setHasMore] = useState(true);

  // Category editing state
  const [editingItem, setEditingItem] = useState(null);
  const [editCategory, setEditCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  
  // Comments expansion state
  const [commentsExpanded, setCommentsExpanded] = useState({});
  const [commentCounts, setCommentCounts] = useState({});
  const [commentData, setCommentData] = useState({});

  // Image modal state
  const [imageModal, setImageModal] = useState({
    isOpen: false,
    imageSrc: '',
    imageAlt: ''
  });

  // New group creation form state
  const [newGroup, setNewGroup] = useState({
    name: '',
    description: '',
    color: 'bg-blue-500',
    is_public: true,
    auto_approve_join: true
  });

  // Modal ref for focus management
  const modalRef = useRef(null);

  const fetchPublicItems = async (page = 1, search = searchTerm, filterParams = filters, append = false) => {
    try {
      const params = {
        page,
        limit: pagination.items_per_page,
      };
      
      // Only include content_type if it has a value
      if (filterParams.content_type && filterParams.content_type.trim() !== '') {
        params.content_type = filterParams.content_type;
      }
      
      if (search) {
        params.search = search;
      }

      const response = await clipboardService.getPublicItems(params);
      
      // Use the real category field from the API
      const itemsWithGroups = (response.items || []).map(item => ({
        ...item,
        group: item.category_name || item.category || 'Uncategorized'
      }));
      
      // Update items - append for infinite scroll, replace for new searches/filters
      setItems(prevItems => {
        if (append) {
          return [...prevItems, ...itemsWithGroups];
        }
        return itemsWithGroups;
      });
      
      setPagination(response.pagination);
      
      // Update hasMore based on pagination
      if (response.pagination) {
        setHasMore(response.pagination.current_page < response.pagination.total_pages);
      } else {
        setHasMore(false);
      }

      // Update category item counts
      updateCategoryCounts(itemsWithGroups);
    } catch (error) {
      console.error('Error fetching public items:', error);
      toast.error('Failed to load public items');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleEditCategory = (item) => {
    setEditingItem(item.id);
    setEditCategory(item.category_id || '');
  };

  const handleSaveCategory = async (itemId) => {
    try {
      // Call the API to update the category
      const response = await clipboardService.updateItem(itemId, {
        category_id: editCategory
      });
      
      // Find the selected category details
      const selectedCategory = categories.find(cat => cat.id.toString() === editCategory.toString());
      
      // Update local state with the response
      setItems(prevItems =>
        prevItems.map(item =>
          item.id === itemId
            ? {
                ...item,
                category_id: editCategory,
                category_name: selectedCategory?.name || '',
                category_description: selectedCategory?.description || '',
                category_color: selectedCategory?.color || '',
                group: selectedCategory?.name || 'Uncategorized'
              }
            : item
        )
      );
      
      setEditingItem(null);
      setEditCategory('');
      toast.success('Category updated successfully!');
    } catch (error) {
      console.error('Error updating category:', error);
      toast.error('Failed to update category');
    }
  };

  const handleCancelEdit = () => {
    setEditingItem(null);
    setEditCategory('');
  };

  const fetchCategories = async () => {
    try {
      setCategoriesLoading(true);
      const response = await clipboardService.getCategories();
      setCategories(response.categories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Failed to load categories');
    } finally {
      setCategoriesLoading(false);
    }
  };

  const fetchCategoriesForSidebar = async () => {
    try {
      setCategoriesLoading(true);
      const response = await clipboardService.getCategories();
      
      // Transform API data to match our expected format
      const transformedCategories = (response.categories || []).map(category => {
        const categoryData = {
          id: category.id || category.name?.toLowerCase().replace(/\s+/g, '-'),
          name: category.name || 'Unnamed Category',
          description: category.description || 'No description available',
          icon: FolderOpen, // Standard icon for all categories
          color: getCategoryColor(category.id || category.name || ''),
          member_count: category.member_count || 0,
          item_count: category.item_count || 0,
          is_default: category.is_default || false,
          created_by: category.created_by || 'Unknown',
          created_at: category.created_at || new Date().toISOString().split('T')[0]
        };
        
        return categoryData;
      });
      
      // Add "All Content" category at the beginning
      const allCategories = [
        { 
          id: 'all', 
          name: 'All Content', 
          description: 'Everything shared by the community',
          icon: Layers, 
          color: 'bg-gray-500', 
          member_count: 0,
          item_count: 0,
          is_default: true
        },
        ...transformedCategories
      ];
      
      setCategories(allCategories);
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Failed to load categories');
      
      // Fallback to default categories if API fails
      setCategories([
        { 
          id: 'all', 
          name: 'All Content', 
          description: 'Everything shared by the community',
          icon: Layers, 
          color: 'bg-gray-500', 
          member_count: 0,
          item_count: 0,
          is_default: true
        }
      ]);
    } finally {
      setCategoriesLoading(false);
    }
  };

  // Pre-defined icon mapping for reliability
  const categoryIconMap = {
    'tech': Sparkles,
    'tips': Sparkles,
    'help': Sparkles,
    'web': Code,
    'dev': Code,
    'code': Code,
    'development': Code,
    'design': Palette,
    'art': Palette,
    'creative': Palette,
    'productivity': Zap,
    'work': Zap,
    'business': Zap,
    'news': TrendingUp,
    'update': TrendingUp,
    'announcement': TrendingUp,
    'music': Music,
    'audio': Music,
    'sound': Music,
    'photo': Camera,
    'image': Camera,
    'camera': Camera,
    'book': BookOpen,
    'read': BookOpen,
    'article': BookOpen,
    'game': Gamepad2,
    'play': Gamepad2,
    'fun': Gamepad2,
    'health': Heart,
    'fitness': Heart,
    'wellness': Heart,
    'star': Star,
    'favorite': Star,
    'best': Star,
    'world': Globe,
    'global': Globe,
    'international': Globe,
    'settings': Settings,
    'config': Settings,
    'setup': Settings,
    'user': Users,
    'people': Users,
    'community': Users,
    'time': Clock,
    'schedule': Clock,
    'calendar': Clock,
    'location': MapPin,
    'place': MapPin,
    'map': MapPin,
    'tag': Tag,
    'label': Tag,
    'category': Tag,
    'layer': Layers,
    'stack': Layers,
    'group': Layers,
    'trend': TrendingUp,
    'popular': TrendingUp,
    'viral': TrendingUp,
    'down': TrendingDown,
    'decrease': TrendingDown,
    'fall': TrendingDown,
    'award': Award,
    'achievement': Award,
    'recognition': Award,
    'message': MessageCircle,
    'chat': MessageCircle,
    'communication': MessageCircle,
    'plus': Plus,
    'add': Plus,
    'new': Plus,
    'more': MoreHorizontal,
    'extra': MoreHorizontal,
    'additional': MoreHorizontal,
    'share': Share2,
    'social': Share2,
    'network': Share2,
    'bookmark': Bookmark,
    'save': Bookmark,
    'eye': Eye,
    'view': Eye,
    'watch': Eye,
    'grid': Grid,
    'layout': Grid,
    'dashboard': Grid,
    'list': List,
    'menu': List,
    'items': List,
    'chevron': ChevronUp,
    'arrow': ChevronUp,
    'direction': ChevronUp,
    'hash': Hash,
    'number': Hash,
    'pound': Hash,
    'userplus': UserPlus,
    'invite': UserPlus,
    'join': UserPlus
  };

  const getCategoryIcon = (categoryName) => {
    if (!categoryName) return FolderOpen;
    
    const name = categoryName.toLowerCase();
    
    // Check for exact matches first
    for (const [keyword, icon] of Object.entries(categoryIconMap)) {
      if (name.includes(keyword)) {
        return icon;
      }
    }
    
    // Default fallback - always return a valid icon
    return FolderOpen;
  };

  const getCategoryColor = (categoryId) => {
    const colors = [
      'bg-blue-500', 'bg-green-500', 'bg-purple-500', 
      'bg-orange-500', 'bg-red-500', 'bg-pink-500',
      'bg-indigo-500', 'bg-teal-500', 'bg-yellow-500'
    ];
    const index = categoryId.toString().length % colors.length;
    return colors[index];
  };

  const updateCategoryCounts = (items) => {
    setCategories(prev => prev.map(category => {
      if (category.id === 'all') {
        return {
          ...category,
          item_count: items.length
        };
      }
      
      // Count items that belong to this category
      const categoryItemCount = items.filter(item => {
        const itemCategoryId = item.category_id || item.category;
        const itemCategoryName = item.category_name || item.category;
        
        // Match by ID or name
        return itemCategoryId === category.id || 
               itemCategoryName === category.name ||
               (itemCategoryId && itemCategoryId.toString() === category.id.toString()) ||
               (itemCategoryName && itemCategoryName.toLowerCase() === category.name.toLowerCase());
      }).length;
      
      return {
        ...category,
        item_count: categoryItemCount
      };
    }));
  };

  useEffect(() => {
    fetchPublicItems();
    fetchCategories(); // Fetch categories for editing
    fetchCategoriesForSidebar(); // Fetch categories for sidebar
  }, []);

  // Focus management and keyboard/click handlers for modal
  useEffect(() => {
    if (showCreateGroup) {
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
      
      // Focus the modal container and then the first input
      const timer = setTimeout(() => {
        if (modalRef.current) {
          modalRef.current.focus();
          const firstInput = modalRef.current.querySelector('input[type="text"]');
          if (firstInput) {
            firstInput.focus();
          }
        }
      }, 100);

      // Handle ESC key press
      const handleEscKey = (event) => {
        if (event.key === 'Escape') {
          setShowCreateGroup(false);
        }
      };

      // Add event listeners
      document.addEventListener('keydown', handleEscKey);
      
      return () => {
        clearTimeout(timer);
        document.body.style.overflow = 'unset';
        document.removeEventListener('keydown', handleEscKey);
      };
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [showCreateGroup]);

  // Infinite scroll callback
  const loadMoreItems = useCallback(() => {
    if (!loading && hasMore) {
      const nextPage = pagination.current_page + 1;
      fetchPublicItems(nextPage, searchTerm, filters, true);
    }
  }, [loading, hasMore, pagination.current_page, searchTerm, filters, fetchPublicItems]);

  // Use infinite scroll hook
  const lastElementRef = useInfiniteScroll(loadMoreItems, hasMore, loading);

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, current_page: 1 }));
    setHasMore(true);
    fetchPublicItems(1, searchTerm, filters, false);
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    setPagination(prev => ({ ...prev, current_page: 1 }));
    setHasMore(true);
    fetchPublicItems(1, searchTerm, newFilters, false);
  };

  const handlePageChange = (page) => {
    fetchPublicItems(page, searchTerm, filters, false);
  };

  const handleRefresh = () => {
    setRefreshing(true);
    setPagination(prev => ({ ...prev, current_page: 1 }));
    setHasMore(true);
    fetchPublicItems(1, searchTerm, filters, false);
  };

  const handleCategorySelect = (categoryId) => {
    if (categoryId === 'all') {
      setSelectedGroup(categoryId);
      // Reset to first page when changing categories
      setPagination(prev => ({ ...prev, current_page: 1 }));
    } else {
      // Navigate to category-specific page
      const category = categories.find(cat => cat.id === categoryId);
      if (category) {
        navigate(`/public/category/${encodeURIComponent(category.name)}`);
      }
    }
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    if (!newGroup.name.trim() || !newGroup.description.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      // Check if user is admin
      if (!user?.is_admin) {
        toast.error('Only administrators can create categories. Please contact an admin to create a new category.');
        setShowCreateGroup(false);
        setNewGroup({ name: '', description: '', color: 'bg-blue-500', is_public: true, auto_approve_join: true });
        return;
      }

      // Convert Tailwind color classes to hex colors
      const colorMap = {
        'bg-blue-500': '#3B82F6',
        'bg-green-500': '#10B981',
        'bg-purple-500': '#8B5CF6',
        'bg-orange-500': '#F59E0B',
        'bg-red-500': '#EF4444',
        'bg-pink-500': '#EC4899'
      };

      // Prepare category data for API
      const categoryData = {
        name: newGroup.name.trim(),
        description: newGroup.description.trim(),
        color: colorMap[newGroup.color] || '#3B82F6', // Default to blue if not found
        is_public: newGroup.is_public,
        auto_approve_join: newGroup.auto_approve_join
      };

      // Call the API to create the category
      const response = await clipboardService.createCategory(categoryData);
      
      // Add the new category to the local state
      const iconComponent = getCategoryIcon(response.category.name);
      const newCategoryData = {
        id: response.category.id,
        name: response.category.name,
        description: response.category.description,
        icon: iconComponent || FolderOpen, // Ensure we always have a valid icon
        color: newGroup.color, // Use the original Tailwind color class
        member_count: 0,
        item_count: 0,
        is_default: false,
        is_public: newGroup.is_public,
        auto_approve_join: newGroup.auto_approve_join,
        created_by: user?.full_name || 'Anonymous',
        created_at: response.category.created_at
      };

      setCategories(prev => [...prev, newCategoryData]);
      setNewGroup({ name: '', description: '', color: 'bg-blue-500', is_public: true, auto_approve_join: true });
      setShowCreateGroup(false);
      toast.success(`Category "${newGroup.name}" created successfully!`);
    } catch (error) {
      console.error('Error creating category:', error);
      
      if (error.response?.status === 403) {
        toast.error('Only administrators can create categories. Please contact an admin to create a new category.');
      } else if (error.response?.status === 409) {
        toast.error('A category with this name already exists. Please choose a different name.');
      } else {
        toast.error('Failed to create category. Please try again.');
      }
    }
  };

  const handleJoinCategory = (categoryId) => {
    setCategories(prev => prev.map(category => 
      category.id === categoryId 
        ? { ...category, member_count: category.member_count + 1 }
        : category
    ));
    toast.success('Joined category successfully!');
  };

  const handleVoteChange = (itemId, action, newVoteCount) => {
    // Update the item's vote count in local state
    setItems(prevItems => 
      prevItems.map(item => 
        item.id === itemId 
          ? { ...item, vote_count: newVoteCount }
          : item
      )
    );
  };

  const handleCommentCountUpdate = useCallback((itemId, count, comments) => {
    setCommentCounts(prev => ({
      ...prev,
      [itemId]: count
    }));
    
    // Store comment data for latest date calculation
    setCommentData(prev => ({
      ...prev,
      [itemId]: comments || []
    }));
  }, []);

  const handleImageClick = (imageSrc, imageAlt) => {
    // Don't open image modal if category modal is open
    if (showCreateGroup) return;
    
    setImageModal({
      isOpen: true,
      imageSrc,
      imageAlt
    });
  };

  const handleCloseImageModal = () => {
    setImageModal({
      isOpen: false,
      imageSrc: '',
      imageAlt: ''
    });
  };

  const getContentTypeColor = (contentType) => {
    const colorMap = {
      text: 'bg-blue-100 text-blue-800',
      html: 'bg-green-100 text-green-800',
      url: 'bg-purple-100 text-purple-800',
      image: 'bg-pink-100 text-pink-800',
      code: 'bg-orange-100 text-orange-800',
      document: 'bg-indigo-100 text-indigo-800'
    };
    return colorMap[contentType] || 'bg-gray-100 text-gray-800';
  };

  const filteredItems = items; // Always show all items on the main public page

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-indigo-600" />
            <p className="text-gray-600">Loading content...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <Link to="/" className="flex flex-col items-start hover:opacity-80 transition-opacity">
                <img src="/big-clipboard-text-logo.jpg" alt="Big Clipboard Logo" className="h-10 w-auto" />
                <span className="text-sm text-primary-700 font-medium mt-1">Copy Once. Use Anywhere.</span>
              </Link>
            </div>
            <div className="flex items-center space-x-4">
              <Link to="/dashboard" className="btn-secondary flex items-center">
                <FileText className="h-4 w-4 mr-2" />
                My Clipboard
              </Link>
                          {(user?.is_admin === true || user?.is_admin === "true" || user?.is_admin === 1) && (
              <Link to="/admin" className="bg-red-100 hover:bg-red-200 text-red-700 border-red-300 px-4 py-2 rounded-lg flex items-center transition-colors">
                <Settings className="h-4 w-4 mr-2" />
                Admin
              </Link>
            )}
              <button
                onClick={() => window.location.href = '/logout'}
                className="btn-secondary flex items-center"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar - Categories */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sticky top-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">Categories</h2>
                {/* {(user?.is_admin === true || user?.is_admin === "true" || user?.is_admin === 1) && (
          <button
            onClick={() => {
              setShowCreateGroup(true);
              // Close image modal if it's open
              if (imageModal.isOpen) {
                setImageModal({
                  isOpen: false,
                  imageSrc: '',
                  imageAlt: ''
                });
              }
            }}
            className="flex items-center space-x-1 px-3 py-1.5 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>Create</span>
          </button>
        )} */}
              </div>

              {/* Categories List */}
              <div className="space-y-2">
                {categoriesLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
                    <span className="ml-2 text-gray-600">Loading categories...</span>
                  </div>
                ) : categories.length === 0 ? (
                  <div className="text-center py-8">
                    <FolderOpen className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                    <p className="text-sm text-gray-500">No categories available</p>
                  </div>
                ) : (
                  categories.map((category) => {
                    return (
                      <button
                        key={category.id}
                        onClick={() => handleCategorySelect(category.id)}
                        className={`w-full text-left p-2 rounded-lg transition-all duration-200 ${
                          selectedGroup === category.id
                            ? 'bg-indigo-50 border-2 border-indigo-200'
                            : 'bg-gray-50 hover:bg-gray-100 border-2 border-transparent'
                        }`}
                      >
                        <div className="flex items-center space-x-2">
                          <div className={`w-6 h-6 ${category.color} rounded-md flex items-center justify-center flex-shrink-0`}>
                            {(() => {
                              const IconComponent = getCategoryIcon(category.name);
                              return <IconComponent className="w-3 h-3 text-white" />;
                            })()}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-medium text-gray-900 truncate text-sm">
                              {category.name}
                            </h3>
                          </div>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>

              {/* Create Category Modal */}
              {showCreateGroup && (
                <>
                  {/* Backdrop to prevent background interaction */}
                  <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-[99999]" />
                  <div className="fixed inset-0 flex items-center justify-center z-[100000]" onClick={() => setShowCreateGroup(false)}>
                    <div ref={modalRef} className="bg-white rounded-xl p-6 w-full max-w-lg mx-4 relative z-[100001]" onClick={(e) => e.stopPropagation()} tabIndex={-1} autoFocus>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Create New Category</h3>
                    <form onSubmit={handleCreateCategory} className="space-y-4">
                                              <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Category Name
                          </label>
                        <input
                          type="text"
                          value={newGroup.name}
                          onChange={(e) => setNewGroup(prev => ({ ...prev, name: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          placeholder="e.g., Tech Tips"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Description
                        </label>
                        <textarea
                          value={newGroup.description}
                          onChange={(e) => setNewGroup(prev => ({ ...prev, description: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          rows={3}
                          placeholder="Describe what this group is about..."
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Color Theme
                        </label>
                        <select
                          value={newGroup.color}
                          onChange={(e) => setNewGroup(prev => ({ ...prev, color: e.target.value }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        >
                          <option value="bg-blue-500">Blue</option>
                          <option value="bg-green-500">Green</option>
                          <option value="bg-purple-500">Purple</option>
                          <option value="bg-orange-500">Orange</option>
                          <option value="bg-red-500">Red</option>
                          <option value="bg-pink-500">Pink</option>
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Privacy Setting
                        </label>
                        <select
                          value={newGroup.is_public ? 'public' : 'private'}
                          onChange={(e) => setNewGroup(prev => ({ 
                            ...prev, 
                            is_public: e.target.value === 'public' 
                          }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        >
                          <option value="public">Public - Anyone can see content</option>
                          <option value="private">Private - Only members can see content</option>
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Join Setting
                        </label>
                        <select
                          value={newGroup.auto_approve_join ? 'auto' : 'approval'}
                          onChange={(e) => setNewGroup(prev => ({ 
                            ...prev, 
                            auto_approve_join: e.target.value === 'auto' 
                          }))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        >
                          <option value="auto">Free to join - Anyone can join immediately</option>
                          <option value="approval">Requires approval - Owner must approve new members</option>
                        </select>
                      </div>
                      <div className="flex space-x-3 pt-4">
                        <button
                          type="button"
                          onClick={() => setShowCreateGroup(false)}
                          className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          Cancel
                        </button>
                                                  <button
                            type="submit"
                            className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                          >
                            Create Category
                          </button>
                      </div>
                    </form>
                  </div>
                </div>
              </>
              )}
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Search and Filters */}
            {!showCreateGroup && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
              <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="text"
                      placeholder="Search content..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>
                </div>
                
                <div className="flex gap-3">
                  <select
                    value={filters.sort_by}
                    onChange={(e) => handleFilterChange('sort_by', e.target.value)}
                    className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="latest">Latest</option>
                    <option value="popular">Most Popular</option>
                    <option value="trending">Trending</option>
                  </select>
                  
                  <button
                    type="submit"
                    className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                  >
                    Search
                  </button>
                </div>
              </form>
            </div>
            )}

            {/* Content Grid */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-6">
                                             <h3 className="text-xl font-semibold text-gray-900">
              All Public Content
              <span className="text-gray-500 font-normal ml-2">({filteredItems.length} items)</span>
            </h3>
                <button
                  onClick={handleRefresh}
                  disabled={refreshing}
                  className="flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                >
                  {refreshing ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4" />
                  )}
                  <span>Refresh</span>
                </button>
              </div>
              
              {filteredItems.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FolderOpen className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No content found</h3>
                  <p className="text-gray-500">Try adjusting your search or filters</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {filteredItems.map((item, index) => (
                    <div 
                      key={item.id} 
                      ref={index === filteredItems.length - 1 ? lastElementRef : null}
                      className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-300"
                    >
                      {/* Header */}
                      <div className="p-4 border-b border-gray-100">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center space-x-2">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getContentTypeColor(item.content_type)}`}>
                              {item.content_type?.toUpperCase() || 'TEXT'}
                            </span>
                            {/* Group/Category Badge */}
                            {item.group && (
                              <div className="flex items-center space-x-2">
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
                                  {item.group}
                                </span>
                                {/* Edit button for item owner */}
                                {user && item.user_id === user.id && (
                                  <button
                                    onClick={() => handleEditCategory(item)}
                                    className="p-1 text-gray-400 hover:text-indigo-600 transition-colors"
                                    title="Edit category"
                                  >
                                    <Settings className="w-3 h-3" />
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center">
                            <HorizontalVoteButtons
                              itemId={item.id}
                              initialVotes={item.votes || 0}
                              onVoteChange={handleVoteChange}
                            />
                          </div>
                        </div>
                        
                        {/* Author Info */}
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full flex items-center justify-center">
                            <span className="text-white font-semibold text-sm">
                              {item.author_name ? item.author_name.charAt(0).toUpperCase() : 'U'}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-900 truncate">
                              {item.author_name || 'Anonymous'}
                            </div>
                            <div className="flex items-center space-x-2 text-xs text-gray-500">
                              <Clock className="w-3 h-3" />
                              <span>{new Date(item.created_at).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                              })}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Content */}
                      <div className="p-4">
                        {item.title && (
                          <h3 className="font-semibold text-gray-900 mb-3 line-clamp-2">
                            {item.title}
                          </h3>
                        )}
                        
                        {/* Source URL */}
                        {item.source_url && (
                          <div className="mb-3">
                            <a
                              href={item.source_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-indigo-600 hover:text-indigo-800 underline break-all"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {item.source_url}
                            </a>
                          </div>
                        )}
                        
                        <div className="text-sm text-gray-700 line-clamp-3">
                          {item.content_type === 'html' ? (
                            <div className="space-y-2">
                              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 min-h-[200px]">
                                <div 
                                  className="html-content"
                                  dangerouslySetInnerHTML={{ __html: item.content }}
                                />
                              </div>
                              <p className="text-xs text-gray-500 text-center">
                                HTML Content
                              </p>
                            </div>
                          ) : item.content_type === 'image' ? (
                            <div className="space-y-3">
                              <div className={`relative group ${!showCreateGroup ? 'cursor-pointer' : 'cursor-default'}`}>
                                <img 
                                  src={item.content} 
                                  alt={item.title || 'Shared image'}
                                  className={`w-full h-64 object-contain rounded-lg border border-gray-200 bg-gray-50 transition-colors ${!showCreateGroup ? 'group-hover:border-indigo-300' : ''}`}
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'block';
                                  }}
                                  onClick={() => handleImageClick(item.content, item.title || 'Shared image')}
                                />
                                {/* Overlay with zoom icon - only show when category modal is closed */}
                                {!showCreateGroup && (
                                  <div 
                                    className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 rounded-lg flex items-center justify-center cursor-pointer"
                                    onClick={() => handleImageClick(item.content, item.title || 'Shared image')}
                                  >
                                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-white bg-opacity-90 rounded-full p-2">
                                      <ZoomIn className="w-6 h-6 text-gray-700" />
                                    </div>
                                  </div>
                                )}
                              </div>
                              <div 
                                className="hidden w-full h-64 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 text-sm"
                              >
                                <div className="text-center">
                                  <Eye className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                                  <p>Image not available</p>
                                </div>
                              </div>
                            </div>
                          ) : item.content_type === 'link' && item.content.match(/^https?:\/\//) ? (
                            <div className="space-y-3">
                              <div className="text-sm">
                                <a
                                  href={item.content}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:text-blue-800 underline break-all"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {item.content}
                                </a>
                              </div>
                              <LinkPreview url={item.content} />
                            </div>
                          ) : (
                            item.content
                          )}
                        </div>
                        
                        {/* Tags */}
                        {item.tags && Array.isArray(item.tags) && item.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-3">
                            {item.tags.slice(0, 3).map((tag, index) => (
                              <span
                                key={index}
                                className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-indigo-100 text-indigo-800"
                              >
                                <Hash className="w-3 h-3 mr-1" />
                                {tag}
                              </span>
                            ))}
                            {item.tags.length > 3 && (
                              <span className="text-xs text-gray-500">
                                +{item.tags.length - 3} more
                              </span>
                            )}
                          </div>
                        )}

                        {/* Inline Category Editor */}
                        {editingItem === item.id && (
                          <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                            <div className="flex items-center space-x-2">
                              <select
                                value={editCategory}
                                onChange={(e) => setEditCategory(e.target.value)}
                                className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                disabled={categoriesLoading}
                              >
                                <option value="">Select a category...</option>
                                {categories.map((category) => (
                                  <option key={category.id} value={category.id}>
                                    {category.name}
                                  </option>
                                ))}
                              </select>
                              <button
                                onClick={() => handleSaveCategory(item.id)}
                                disabled={!editCategory || categoriesLoading}
                                className="px-3 py-1.5 bg-indigo-600 text-white text-sm rounded-md hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                Save
                              </button>
                              <button
                                onClick={handleCancelEdit}
                                className="px-3 py-1.5 bg-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-400 transition-colors"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {/* Comments Footer */}
                      {!showCreateGroup && (
                        <div className="px-4 py-3 bg-gray-50 border-t border-gray-100">
                          {/* Comments Summary Toggle */}
                          <button
                            onClick={() => setCommentsExpanded(prev => ({ ...prev, [item.id]: !prev[item.id] }))}
                            className="w-full flex items-center justify-between text-xs text-gray-500 hover:text-gray-700 transition-colors"
                          >
                            <div className="flex items-center space-x-4">
                              <span className="flex items-center space-x-1">
                                <MessageCircle className="w-3 h-3" />
                                <span>{commentCounts[item.id] || 0} comments</span>
                              </span>
                              {commentCounts[item.id] > 0 && commentData[item.id] && commentData[item.id].length > 0 && (
                                <span className="flex items-center space-x-1">
                                  <Clock className="w-3 h-3" />
                                  <span>Latest: {new Date(commentData[item.id][0]?.created_at || Date.now()).toLocaleDateString()}</span>
                                </span>
                              )}
                            </div>
                            <div className="flex items-center space-x-1">
                              <span className="text-xs text-gray-500 group-hover:text-blue-500 transition-colors duration-200">
                                {commentsExpanded[item.id] ? 'Click to hide' : (commentCounts[item.id] || 0) === 0 ? 'Be the first to comment!' : 'Click to view and comment'}
                              </span>
                            </div>
                          </button>
                          
                          {/* Comments Section - Always render to fetch data, but conditionally show content */}
                          <div className={commentsExpanded[item.id] ? 'mt-3' : 'hidden'}>
                            <CommentSection
                              itemId={item.id}
                              initialComments={item.comments || []}
                              hideToggle={true}
                              onCommentCountUpdate={handleCommentCountUpdate}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                  
                  {/* Infinite scroll loading indicator */}
                  {hasMore && (
                    <div className="col-span-full text-center py-4">
                      <Loader2 className="w-6 h-6 animate-spin mx-auto text-indigo-600" />
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Items count info */}
            {filteredItems.length > 0 && (
              <div className="flex justify-center mt-8">
                <div className="text-sm text-gray-600">
                  Showing {filteredItems.length} of {pagination.total_items || filteredItems.length} items
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Image Modal - Only show when no other modals are open */}
      {!showCreateGroup && (
        <ImageModal
          isOpen={imageModal.isOpen}
          imageSrc={imageModal.imageSrc}
          imageAlt={imageModal.imageAlt}
          onClose={handleCloseImageModal}
        />
      )}
    </div>
  );
};

export default PublicPage5;